/**
 * Card Component - Wrapper minimal pour Carbon Tile
 *
 * ⚠️ NOTE: Ce composant est un wrapper de compatibilité pour faciliter la migration.
 * Pour les nouveaux composants, utilisez directement Tile ou ClickableTile de Carbon.
 *
 * @example
 * ```tsx
 * // Utilisation directe Carbon (recommandé)
 * import { Tile, ClickableTile } from '@carbon/react';
 *
 * <Tile>
 *   <h3>Titre</h3>
 *   <p>Contenu</p>
 * </Tile>
 *
 * // Utilisation wrapper (pour compatibilité)
 * import { Card, CardContent, CardHeader, CardTitle } from '@sphere/shared/ui/design-system';
 *
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Titre</CardTitle>
 *   </CardHeader>
 *   <CardContent>Contenu</CardContent>
 * </Card>
 * ```
 */

import * as React from 'react'
import { Tile, ClickableTile } from '@carbon/react'
import { cn } from '@sphere/shared/util'

export interface CardProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Rendre la carte cliquable (utilise ClickableTile au lieu de Tile)
   */
  onClick?: () => void
  /**
   * Classes CSS additionnelles
   */
  className?: string
}

/**
 * Card - Wrapper pour Carbon Tile/ClickableTile
 * Utilise Tile si pas d'onClick, ClickableTile sinon
 */
export const Card = React.forwardRef<
  HTMLDivElement | HTMLAnchorElement,
  CardProps
>(({ className, onClick, children, ...props }, ref) => {
  if (onClick) {
    return (
      <ClickableTile
        ref={ref as React.Ref<HTMLAnchorElement>}
        onClick={onClick}
        className={cn('cds--tile', className)}
        {...props}
      >
        {children}
      </ClickableTile>
    )
  }

  return (
    <Tile
      ref={ref as React.Ref<HTMLDivElement>}
      className={cn('cds--tile', className)}
      {...props}
    >
      {children}
    </Tile>
  )
})
Card.displayName = 'Card'

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
}

/**
 * CardHeader - Section header de la carte
 */
export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('mb-4 pb-4 border-b border-border', className)}
        {...props}
      />
    )
  }
)
CardHeader.displayName = 'CardHeader'

export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  className?: string
}

/**
 * CardTitle - Titre de la carte
 */
export const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, ...props }, ref) => {
    return (
      <h3
        ref={ref}
        className={cn('text-lg font-semibold leading-none', className)}
        {...props}
      />
    )
  }
)
CardTitle.displayName = 'CardTitle'

export interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  className?: string
}

/**
 * CardDescription - Description de la carte
 */
export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  CardDescriptionProps
>(({ className, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
})
CardDescription.displayName = 'CardDescription'

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
}

/**
 * CardContent - Contenu principal de la carte
 */
export const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn('', className)} {...props} />
  }
)
CardContent.displayName = 'CardContent'

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
}

/**
 * CardFooter - Footer de la carte
 */
export const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('mt-4 pt-4 border-t border-border', className)}
        {...props}
      />
    )
  }
)
CardFooter.displayName = 'CardFooter'
